/**
 * Session expiry redirect.
 *
 * Mounted once in the application shell, it is the only place that reacts to a
 * rejected credential: the user cache is dropped and the visitor is sent to the
 * sign-in page with the current address as the return target. Keeping it as a
 * component (rather than inside the API client) is what lets it use the router
 * and the query cache.
 */

import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { loginRedirectPath } from '../../lib/login-redirect'
import { queryClient } from '../../lib/query'
import { onSessionExpired } from '../../lib/session-events'
import { useSessionStore } from '../../stores/session'

export function SessionExpiryRedirect() {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    return onSessionExpired(() => {
      const { user } = useSessionStore.getState()
      useSessionStore.getState().clear()

      // Answers cached for the previous identity must not be shown again, even
      // when no redirect follows: the rejected request may have raced a public
      // page's own read of the same cache.
      queryClient.clear()

      // An anonymous visitor can only reach this through a public page whose
      // request raced the boot check; there is nothing to recover, so no
      // redirect is forced on them.
      if (!user || location.pathname.startsWith('/login')) {
        return
      }

      navigate(loginRedirectPath(location.pathname, location.search), { replace: true })
    })
  }, [location.pathname, location.search, navigate])

  return null
}
